import { useEffect, useState, type ReactNode } from "react";

import { IconButton } from "@/components/ui/icon-button";
import { MuiArrowDropDownIcon } from "@/components/ui/mui-arrow-icons";
import { MuiArrowDropUpIcon } from "@/components/ui/mui-spec-icons";
import { MuiSwitch } from "@/components/ui/mui-switch";
import { SearchField } from "@/components/ui/search-field";
import type { SpecGroupExpansion } from "@/hooks/spec-lines";
import { useDebouncedValue } from "@/hooks/use-debounced-value";
import { MuiTypography } from "@/components/ui/mui-typography";
import { cn } from "@/lib/utils";

// The lines toolbar — the donor's `SearchSpecLines`, `ExpandCollapseGroups`,
// `SwitchChanges` and `SwitchTargetCost` (`shared/components/`), laid out as
// the island lays them (`SpecDetailsIsland.tsx:186-211`) (#695). Two rows, each
// an item of the details frame's header stack (gap 8): the search box, then the
// controls — Expand/Collapse, a spacer, the switches, 8px apart.
//
// **The Editor mounts it too**, so it reads nothing from the route: the term
// comes in, the debounced term goes out, and the page decides what writing it
// means (the details page writes `?specSearch=`). The Editor adds two things,
// each a prop the details page leaves unset (#806): its `Add group` to the
// box's right (`searchAction`, the donor's `SearchSpecLines` child), and the
// target-cost switch's label for a core or base spec (`targetCostLabel`).
//
// **The target-cost switch** (`switch-target-cost.tsx`, #1216) is live: its
// state is the page's (`useTargetCostSwitch`), on by default, and its tooltip
// the donor's, verbatim, over the whole control.

/**
 * The line search's debounce — `useDebounce(watch('search'), 300)`
 * (`SearchSpecLines.tsx:18`).
 */
const SPEC_LINE_SEARCH_DEBOUNCE_MS = 300;

/** `switch-target-cost.tsx:32`, verbatim — the double space is the donor's. */
const TARGET_COST_SWITCH_TOOLTIP =
  "Items in this specification that are over target cost  will be called out.";

interface SpecLinesToolbarProps {
  /** `?specSearch=` as the page arrived with it, or as it now stands. */
  urlSearch: string;
  /** The debounced term, whenever it differs from `urlSearch`; `""` is none. */
  onSearch: (term: string) => void;
  /** A new term's first page is on its way: the control keeps its space, unseen. */
  isSearching: boolean;
  /** No group drawn, a failed read included: the control keeps its space, unseen. */
  isEmpty: boolean;
  /** The expansion map's half the Expand/Collapse control reads and drives. */
  expansion: Pick<
    SpecGroupExpansion,
    "hasExpandedGroups" | "onCollapseAllGroups" | "onExpandAllGroups"
  >;
  /** Whether "Show changes from Base" is offered at all. */
  showChangesSwitch: boolean;
  showChanges: boolean;
  onShowChangesChange: (next: boolean) => void;
  /** Whether the target-cost switch is offered. */
  showTargetCostSwitch: boolean;
  showTargetCost: boolean;
  onShowTargetCostChange: (next: boolean) => void;
  /** The target-cost switch's label; "Show Target Cost" unless given. */
  targetCostLabel?: string;
  /** Beside the search box, to its right — the Editor's `Add group`. */
  searchAction?: ReactNode;
}

export function SpecLinesToolbar(props: SpecLinesToolbarProps) {
  return (
    <>
      {props.searchAction ? (
        // `SearchSpecLines.tsx:36-44`: a column, then a row from 600px, gap 16,
        // top-aligned; the box grows.
        <div className="flex w-full flex-col items-start gap-4 min-[600px]:flex-row">
          <div className="flex w-full min-w-0 flex-1">
            <SpecLineSearch urlSearch={props.urlSearch} onSearch={props.onSearch} />
          </div>
          {props.searchAction}
        </div>
      ) : (
        <SpecLineSearch urlSearch={props.urlSearch} onSearch={props.onSearch} />
      )}
      <div data-testid="spec-lines-controls" className="flex flex-row items-center gap-2">
        <ExpandCollapseGroups
          hidden={props.isSearching || props.isEmpty}
          hasExpandedGroups={props.expansion.hasExpandedGroups}
          onCollapseAllGroups={props.expansion.onCollapseAllGroups}
          onExpandAllGroups={props.expansion.onExpandAllGroups}
        />
        <div className="flex-1" />
        {props.showChangesSwitch ? (
          <MuiSwitch
            label=" Show changes from Base "
            checked={props.showChanges}
            onCheckedChange={props.onShowChangesChange}
            tooltip="Rows changed from original will be highlighted."
          />
        ) : null}
        {props.showTargetCostSwitch ? (
          <MuiSwitch
            label={props.targetCostLabel ?? "Show Target Cost"}
            checked={props.showTargetCost}
            onCheckedChange={props.onShowTargetCostChange}
            tooltip={TARGET_COST_SWITCH_TOOLTIP}
          />
        ) : null}
      </div>
    </>
  );
}

/**
 * The search box — `SearchSpecLines` over `SpecSheetLineFilters`: seeded from
 * the URL **once** (react-hook-form's `defaultValues`), 300ms of debounce, and
 * a write only when the debounced term differs from the URL's. It never reads
 * the URL again, so after Back the debounce writes the typed term back — the
 * donor's behaviour, carried. The term is sent untrimmed. A `<form>` whose
 * submit does nothing, so Enter is inert.
 */
function SpecLineSearch({
  urlSearch,
  onSearch,
}: {
  urlSearch: string;
  onSearch: (term: string) => void;
}) {
  const [value, setValue] = useState(urlSearch);
  const debounced = useDebouncedValue(value, SPEC_LINE_SEARCH_DEBOUNCE_MS);

  useEffect(() => {
    if (urlSearch !== debounced) onSearch(debounced);
    // `onSearch` is the page's writer, not part of the condition.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debounced, urlSearch]);

  return (
    <form
      data-testid="search-input"
      onSubmit={(event) => event.preventDefault()}
      className="flex w-full"
    >
      <SearchField
        value={value}
        onChange={setValue}
        onClear={() => setValue("")}
        placeholder="Search for Group, Category or Line"
        aria-label="Search for Group, Category or Line"
        data-testid="input-search-field"
      />
    </form>
  );
}

/**
 * `ExpandCollapseGroups` (`:16-35`): a `body1` label and a medium icon button,
 * 4px apart — "Collapse all" and the up arrow while any group is expanded,
 * "Expand all" and the down arrow otherwise. `visibility: hidden`, keeping its
 * space, while a search loads or there are no results.
 */
function ExpandCollapseGroups({
  hidden,
  hasExpandedGroups,
  onCollapseAllGroups,
  onExpandAllGroups,
}: {
  hidden: boolean;
  hasExpandedGroups: boolean;
  onCollapseAllGroups: () => void;
  onExpandAllGroups: () => void;
}) {
  return (
    <div
      data-testid="expand-collapse-groups"
      className={cn("flex flex-row items-center gap-1", hidden && "invisible")}
    >
      <MuiTypography className="pl-2">
        {hasExpandedGroups ? "Collapse all" : "Expand all"}
      </MuiTypography>
      <IconButton
        aria-label="Expand/Collapse all groups"
        data-testid="expand-collapse-btn"
        onClick={hasExpandedGroups ? onCollapseAllGroups : onExpandAllGroups}
      >
        {hasExpandedGroups ? <MuiArrowDropUpIcon /> : <MuiArrowDropDownIcon />}
      </IconButton>
    </div>
  );
}
